<template>
  <div class="pt-3">
    <v-expansion-panels v-model="panelAbierto" multiple accordion>
      <v-expansion-panel v-for="tipo in tiposObservacion" :key="tipo.value">
        <v-expansion-panel-header>
          <div class="tipo-chip">
            <v-icon small class="mr-2" color="secondary">{{ tipo.icono }}</v-icon>
            <span class="tipo-label">{{ tipo.label }}</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="observacion-base mb-3">
            <template v-if="!tipo.observacionBase">
              <span class="sin-contenido">Sin observaciones registradas</span>
            </template>
            <template v-else>
              <div class="observacion-texto">{{ tipo.observacionBase }}</div>
            </template>
          </div>

          <div class="d-flex justify-space-between align-center mb-2">
            <span class="seguimientos-titulo">Seguimientos</span>
            <v-btn small text color="secondary" @click="abrirFormularioCrear(tipo.value)">
              <v-icon small left>mdi-plus</v-icon>
              Agregar seguimiento
            </v-btn>
          </div>

          <v-progress-linear v-if="loading" indeterminate color="secondary" class="mb-2"></v-progress-linear>

          <template v-else>
            <div v-if="seguimientosPorTipo(tipo.value).length === 0" class="sin-contenido">
              Sin seguimientos registrados
            </div>
            <v-list v-else dense class="transparent">
              <v-list-item v-for="seguimiento in seguimientosPorTipo(tipo.value)" :key="seguimiento.idSeguimiento">
                <v-list-item-content>
                  <v-list-item-title class="seguimiento-texto">{{ seguimiento.seguimiento }}</v-list-item-title>
                  <v-list-item-subtitle v-if="seguimiento.fecha">{{ seguimiento.fecha }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="flex-row">
                  <v-btn v-if="seguimiento.adjunto" icon small color="info" @click="descargarAdjunto(seguimiento)">
                    <v-icon small>mdi-paperclip</v-icon>
                  </v-btn>
                  <v-btn icon small color="secondary" @click="abrirFormularioEditar(tipo.value, seguimiento)">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small color="error" @click="abrirDialogoEliminar(tipo.value, seguimiento)">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </template>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="dialogFormulario" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6">{{ editando ? 'Editar seguimiento' : 'Nuevo seguimiento' }}</v-card-title>
        <v-card-text>
          <v-form ref="formSeguimiento" v-model="formularioValido">
            <v-textarea
              v-model="textoSeguimiento"
              label="Seguimiento"
              no-resize
              rows="3"
              color="secondary"
              :rules="[(v) => !!v || 'Este campo es requerido.']"
            ></v-textarea>
            <v-file-input
              v-model="archivoAdjunto"
              label="Adjunto (opcional)"
              color="secondary"
              prepend-icon="mdi-paperclip"
              :rules="reglasAdjunto"
              show-size
            ></v-file-input>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cerrarFormulario">Cancelar</v-btn>
          <v-btn
            color="secondary"
            :loading="loadingFormulario"
            :disabled="!formularioValido"
            @click="guardarSeguimiento"
          >
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEliminar" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Eliminar seguimiento</v-card-title>
        <v-card-text>¿Estás seguro de que deseas eliminar este seguimiento? Esta acción no se puede deshacer.</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogEliminar = false">Cancelar</v-btn>
          <v-btn color="error" :loading="loadingEliminar" @click="confirmarEliminacion">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { TIPOS_OBSERVACION, TIPOS_OBSERVACION_LABELS } from '@/modules/seguimientos-resultado-c3/domain/tiposObservacion'

const TAMANIO_MAXIMO_ADJUNTO = 10 * 1024 * 1024 // 10MB
const EXTENSIONES_PERMITIDAS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx']

export default {
  name: 'ObservacionesResultadoC3DialogComponent',
  props: {
    resultadoC3: {
      type: Object,
      required: true,
    },
    idPersonal: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      panelAbierto: [0, 1, 2, 3],
      dialogFormulario: false,
      formularioValido: false,
      loadingFormulario: false,
      editando: false,
      tipoObservacionActivo: null,
      idSeguimientoEditando: null,
      textoSeguimiento: '',
      archivoAdjunto: null,
      dialogEliminar: false,
      loadingEliminar: false,
      seguimientoAEliminar: null,
      tipoObservacionAEliminar: null,
      reglasAdjunto: [
        (v) => !v || v.size <= TAMANIO_MAXIMO_ADJUNTO || 'El archivo no debe superar 10MB.',
        (v) => {
          if (!v) return true
          const extension = v.name.split('.').pop().toLowerCase()
          return EXTENSIONES_PERMITIDAS.includes(extension) || 'Tipo de archivo no permitido.'
        },
      ],
    }
  },
  computed: {
    ...mapGetters('seguimientosResultadoC3', {
      isLoadingSeguimientos: 'isLoading',
      getSeguimientosPorTipo: 'getSeguimientosPorTipo',
    }),
    loading() {
      return this.isLoadingSeguimientos
    },
    tiposObservacion() {
      return [
        {
          value: TIPOS_OBSERVACION.GENERALES,
          label: TIPOS_OBSERVACION_LABELS[TIPOS_OBSERVACION.GENERALES],
          icono: 'mdi-file-document-outline',
          observacionBase: this.resultadoC3.observacionesGenerales,
        },
        {
          value: TIPOS_OBSERVACION.ARMERIA,
          label: TIPOS_OBSERVACION_LABELS[TIPOS_OBSERVACION.ARMERIA],
          icono: 'mdi-alpha-a-circle-outline',
          observacionBase: this.resultadoC3.observacionesAdicionalesA,
        },
        {
          value: TIPOS_OBSERVACION.ACADEMIA,
          label: TIPOS_OBSERVACION_LABELS[TIPOS_OBSERVACION.ACADEMIA],
          icono: 'mdi-alpha-b-circle-outline',
          observacionBase: this.resultadoC3.observacionesAdicionalesB,
        },
        {
          value: TIPOS_OBSERVACION.MEDICO,
          label: TIPOS_OBSERVACION_LABELS[TIPOS_OBSERVACION.MEDICO],
          icono: 'mdi-medical-bag',
          observacionBase: this.resultadoC3.observacionesMedicas,
        },
      ]
    },
  },
  methods: {
    ...mapActions('seguimientosResultadoC3', [
      'fetchSeguimientos',
      'createSeguimiento',
      'updateSeguimiento',
      'deleteSeguimiento',
    ]),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    seguimientosPorTipo(tipoObservacion) {
      return this.getSeguimientosPorTipo(tipoObservacion)
    },

    abrirFormularioCrear(tipoObservacion) {
      this.editando = false
      this.tipoObservacionActivo = tipoObservacion
      this.idSeguimientoEditando = null
      this.textoSeguimiento = ''
      this.archivoAdjunto = null
      this.dialogFormulario = true
    },

    abrirFormularioEditar(tipoObservacion, seguimiento) {
      this.editando = true
      this.tipoObservacionActivo = tipoObservacion
      this.idSeguimientoEditando = seguimiento.idSeguimiento
      this.textoSeguimiento = seguimiento.seguimiento
      this.archivoAdjunto = null
      this.dialogFormulario = true
    },

    cerrarFormulario() {
      this.dialogFormulario = false
      if (this.$refs.formSeguimiento) {
        this.$refs.formSeguimiento.resetValidation()
      }
    },

    async guardarSeguimiento() {
      this.loadingFormulario = true
      try {
        const payload = {
          tipoObservacion: this.tipoObservacionActivo,
          seguimiento: this.textoSeguimiento,
          adjunto: this.archivoAdjunto || undefined,
        }
        if (this.editando) {
          await this.updateSeguimiento({
            idPersonal: this.idPersonal,
            idResultado: this.resultadoC3.idResultado,
            idSeguimiento: this.idSeguimientoEditando,
            seguimiento: payload,
          })
        } else {
          await this.createSeguimiento({
            idPersonal: this.idPersonal,
            idResultado: this.resultadoC3.idResultado,
            seguimiento: payload,
          })
        }
        this.dialogFormulario = false
      } catch (error) {
        console.error('[ObservacionesResultadoC3DialogComponent] guardarSeguimiento failed:', error)
        this.SET_SNACKBAR_MESSAGE(
          this.editando ? 'Ocurrió un error al editar el seguimiento.' : 'Ocurrió un error al crear el seguimiento.'
        )
      } finally {
        this.loadingFormulario = false
      }
    },

    abrirDialogoEliminar(tipoObservacion, seguimiento) {
      this.tipoObservacionAEliminar = tipoObservacion
      this.seguimientoAEliminar = seguimiento
      this.dialogEliminar = true
    },

    async confirmarEliminacion() {
      this.loadingEliminar = true
      try {
        await this.deleteSeguimiento({
          idPersonal: this.idPersonal,
          idResultado: this.resultadoC3.idResultado,
          idSeguimiento: this.seguimientoAEliminar.idSeguimiento,
        })
        this.dialogEliminar = false
        this.seguimientoAEliminar = null
      } catch (error) {
        console.error('[ObservacionesResultadoC3DialogComponent] confirmarEliminacion failed:', error)
        this.SET_SNACKBAR_MESSAGE('Ocurrió un error al eliminar el seguimiento.')
      } finally {
        this.loadingEliminar = false
      }
    },

    descargarAdjunto(seguimiento) {
      window.open(seguimiento.adjunto, '_blank')
    },
  },

  watch: {
    'resultadoC3.idResultado': {
      immediate: true,
      async handler(idResultado) {
        if (idResultado) {
          try {
            await this.fetchSeguimientos({ idPersonal: this.idPersonal, idResultado })
          } catch (error) {
            console.error('[ObservacionesResultadoC3DialogComponent] fetchSeguimientos failed:', error)
            this.SET_SNACKBAR_MESSAGE('Ocurrió un error al cargar los seguimientos.')
          }
        }
      },
    },
  },
}
</script>

<style scoped>
.tipo-chip {
  display: flex;
  align-items: center;
}

.tipo-label {
  font-weight: 600;
  font-size: 0.85rem;
  letter-spacing: 0.3px;
  color: #e0e0e0;
}

.observacion-base {
  color: #e0e0e0;
  line-height: 1.6;
  white-space: pre-line;
}

.seguimientos-titulo {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(224, 224, 224, 0.7);
}

.seguimiento-texto {
  white-space: pre-line;
  color: #e0e0e0;
}

.sin-contenido {
  color: rgba(224, 224, 224, 0.4);
  font-style: italic;
  font-size: 0.875rem;
}
</style>
